'use client';

import { createClient } from './client';

export interface Article {
  id: string;
  title: string;
  summary: string;
  imageUrl: string;
  category: string;
  country: string;
  status: 'published' | 'draft' | 'scheduled' | 'archived';
  publishedAt: string | null;
  views: number;
  sourceId: string | null;
  sourceName?: string;
  sourceLogo?: string;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

function convertToArticle(row: any): Article {
  return {
    id: row.id,
    title: row.title,
    summary: row.summary,
    imageUrl: row.image_url,
    category: row.category,
    country: row.country,
    status: row.status,
    publishedAt: row.published_at,
    views: row.views,
    sourceId: row.source_id,
    sourceName: row.news_sources?.name,
    sourceLogo: row.news_sources?.logo,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const articleService = {
  async getAll(filters?: { category?: string; status?: string; limit?: number }): Promise<Article[]> {
    try {
      const supabase = createClient();
      if (!supabase) return [];

      let query = supabase
        .from('news_articles')
        .select('*, news_sources(name, logo)')
        .order('created_at', { ascending: false });

      if (filters?.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data?.map(convertToArticle) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async create(article: {
    title: string;
    summary: string;
    imageUrl: string;
    category: string;
    country: string;
    status: 'published' | 'draft' | 'scheduled';
    sourceId?: string;
  }): Promise<{ success: boolean; data?: Article; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { data, error } = await supabase
        .from('news_articles')
        .insert({
          title: article.title,
          summary: article.summary,
          image_url: article.imageUrl,
          category: article.category,
          country: article.country,
          status: article.status,
          source_id: article.sourceId || null,
          published_at: article.status === 'published' ? new Date().toISOString() : null,
        })
        .select('*, news_sources(name, logo)')
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true, data: convertToArticle(data) };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async update(id: string, article: Partial<{
    title: string;
    summary: string;
    imageUrl: string;
    category: string;
    country: string;
    status: 'published' | 'draft' | 'scheduled' | 'archived';
    sourceId: string;
  }>): Promise<{ success: boolean; data?: Article; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const updateData: any = {};
      if (article.title !== undefined) updateData.title = article.title;
      if (article.summary !== undefined) updateData.summary = article.summary;
      if (article.imageUrl !== undefined) updateData.image_url = article.imageUrl;
      if (article.category !== undefined) updateData.category = article.category;
      if (article.country !== undefined) updateData.country = article.country;
      if (article.status !== undefined) {
        updateData.status = article.status;
        if (article.status === 'published') {
          updateData.published_at = new Date().toISOString();
        }
      }
      if (article.sourceId !== undefined) updateData.source_id = article.sourceId;

      const { data, error } = await supabase
        .from('news_articles')
        .update(updateData)
        .eq('id', id)
        .select('*, news_sources(name, logo)')
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true, data: convertToArticle(data) };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { error } = await supabase
        .from('news_articles')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};